import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { submitIdentityExtraInfo } from '../thunks/submitIdentityExtraInfo';
import { submitApplication } from '../thunks/unifiedApplicationThunks';
import { submitAgreement } from '../thunks/submitAgreement';

type IdentityType = 'LEGAL' | 'PERSONAL';

export interface UnifiedFormState {
  currentStep: number;
  nextStep: number | null;
  serverStepState: string;
  isSubmitting: boolean;
  error: string | null;
  identity: {
    type: IdentityType;
    name: string;
    display_name: string;
    national_id: string;
    address: string;
    postal_code: string;
    phone: string;
    id?: number;
    status?: string;
    content_status?: string;
    active?: number;
    user_id?: number;
    national_id_image?: string | null;
    contract?: string | null;
    avatar?: string | null;
    errors?: any;
    published_applications_count?: number | null;
    step?: number;
  };
  application: {
    publisher_id: string;
    application_id: string;
    fa_name: string;
    en_name: string;
    category: string | null;
    publish_after_accept: string | null;
    description: string | null;
    fa_description: string | null;
    en_description: string | null;
    logo: string | null;
    version_text: string | null;
    application_file_url?: string | null; // فیلد جدید برای ذخیره URL فایل
  };
}

const initialState: UnifiedFormState = {
  currentStep: 1,
  nextStep: null,
  serverStepState: '',
  isSubmitting: false,
  error: null,
  identity: {
    type: 'PERSONAL',
    name: '',
    display_name: '',
    national_id: '',
    address: '',
    postal_code: '',
    phone: '',
    id: undefined,
    status: undefined,
    content_status: undefined,
    active: undefined,
    user_id: undefined,
    national_id_image: null,
    contract: null,
    avatar: null,
    errors: null,
    published_applications_count: null,
    step: undefined,
  },
  application: {
    publisher_id: '',
    application_id: '',
    fa_name: '',
    en_name: '',
    category: null,
    publish_after_accept: null,
    description: null,
    fa_description: null,
    en_description: null,
    logo: null,
    version_text: null,
    application_file_url: null, // مقدار اولیه برای فیلد جدید
  },
};

const unifiedFormSlice = createSlice({
  name: 'unifiedForm',
  initialState,
  reducers: {
    updateIdentityField: <K extends keyof UnifiedFormState['identity']>(
      state: UnifiedFormState,
      action: PayloadAction<{ field: K; value: UnifiedFormState['identity'][K] }>,
    ) => {
      state.identity[action.payload.field] = action.payload.value;
    },
    updateApplicationField: (
      state,
      action: PayloadAction<{ field: keyof UnifiedFormState['application']; value: any }>,
    ) => {
      state.application[action.payload.field] = action.payload.value;
    },
    setIdentityType: (state, action: PayloadAction<IdentityType>) => {
      state.identity.type = action.payload;
      if (action.payload !== state.identity.type) {
        state.identity = { ...initialState.identity, type: action.payload };
      }
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setNextStep: (state, action: PayloadAction<number | null>) => {
      state.nextStep = action.payload;
    },
    setServerStepState: (state, action: PayloadAction<string>) => {
      state.serverStepState = action.payload;
    },
    syncStepWithServer: (
      state,
      action: PayloadAction<{ currentStep: number; nextStep: number | null }>,
    ) => {
      state.currentStep = action.payload.currentStep;
      state.nextStep = action.payload.nextStep;
      state.serverStepState = 'synced';
    },
    incrementStep: (state) => {
      state.currentStep += 1;
    },
    decrementStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },
    setSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetForm: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitIdentityExtraInfo.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitIdentityExtraInfo.fulfilled, (state, action) => {
        state.isSubmitting = false;
        if (action.payload.data) {
          state.identity = { ...state.identity, ...action.payload.data };
          if (action.payload['current-step'] !== undefined) {
            state.currentStep = action.payload['current-step'];
          }
          if (action.payload['next-step'] !== undefined) {
            state.nextStep = action.payload['next-step'];
            state.serverStepState = 'synced';
          }
        }
      })
      .addCase(submitIdentityExtraInfo.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'خطا در ارسال اطلاعات';
      })
      .addCase(submitApplication.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitApplication.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.error = null;
        const appData = action.payload.data || action.payload;
        if (appData) {
          state.application = {
            ...state.application,
            application_id: String(appData.id || ''),
            en_name: appData.name_en || '',
            fa_name: appData.name_fa || appData.name || '',
            category: appData.category_id || null,
            description: appData.description || null,
            fa_description: appData.description_fa || null,
            en_description: appData.description_en || null,
            publisher_id: appData.publisher_id || state.application.publisher_id,
            logo: appData.logo || null,
            version_text: appData.version_text || null,
            application_file_url: appData.application_file_url || state.application.application_file_url,
          };
          if (appData['current-step'] !== undefined) {
            state.currentStep = appData['current-step'];
          }
          if (appData['next-step'] !== undefined) {
            state.nextStep = appData['next-step'];
            state.serverStepState = 'synced';
          }
        }
      })
      .addCase(submitApplication.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'خطا در ارسال اطلاعات اپلیکیشن';
      })
      .addCase(submitAgreement.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitAgreement.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.identity.contract = action.payload.contract || 'accepted';
        if (action.payload['current-step'] !== undefined) {
          state.currentStep = action.payload['current-step'];
        }
        if (action.payload['next-step'] !== undefined) {
          state.nextStep = action.payload['next-step'];
          state.serverStepState = 'synced';
        }
      })
      .addCase(submitAgreement.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  updateIdentityField,
  updateApplicationField,
  setIdentityType,
  setCurrentStep,
  setNextStep,
  setServerStepState,
  syncStepWithServer,
  incrementStep,
  decrementStep,
  setSubmitting,
  setError,
  resetForm,
} = unifiedFormSlice.actions;

export default unifiedFormSlice.reducer;